/**
 * Error Tracker Utility with Sentry Integration
 * Provides comprehensive error tracking with Sentry SDK
 * Including: Error tracking, Performance monitoring, Session Replay
 */

import * as Sentry from "@sentry/react";
import type { ErrorContext } from "../shared/types/utils.types";

class ErrorTracker {
  private initialized = false;
  private sentryEnabled = false;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Initialize Sentry if DSN is provided
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    if (sentryDsn) {
      this.initSentry(sentryDsn);
    } else {
      console.warn(
        "[ErrorTracker] Sentry DSN not found - error tracking disabled",
      );
    }

    // Setup global error handlers (even without Sentry for dev logging)
    if (!this.sentryEnabled) {
      this.setupGlobalHandlers();
    }
  }

  private initSentry(dsn: string) {
    try {
      const environment =
        import.meta.env.VITE_SENTRY_ENVIRONMENT || "production";
      const tracesSampleRate = parseFloat(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1",
      );
      const replaysSessionSampleRate = parseFloat(
        import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0.1",
      );
      const replaysOnErrorSampleRate = parseFloat(
        import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || "1.0",
      );

      Sentry.init({
        dsn,
        environment,

        // Performance Monitoring
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],

        // Performance Traces Sample Rate
        tracesSampleRate,

        // Session Replay
        replaysSessionSampleRate,
        replaysOnErrorSampleRate,

        // Additional configuration
        beforeSend(event, hint) {
          // Filter out errors in development unless explicitly testing Sentry
          if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_TEST_MODE) {
            console.log("[Sentry] Would send error:", event, hint);
            return null; // Don't send in dev
          }
          return event;
        },

        // Ignore common errors that aren't actionable
        ignoreErrors: [
          // Browser extensions
          "top.GLOBALS",
          "chrome-extension://",
          "moz-extension://",
          // Random plugins/extensions
          "originalCreateNotification",
          "canvas.contentDocument",
          "MyApp_RemoveAllHighlights",
          // Network errors
          "NetworkError",
          "Network request failed",
          // Cancelled requests
          "AbortError",
          "Request aborted",
        ],
      });

      this.sentryEnabled = true;
      console.log(
        `[ErrorTracker] Sentry initialized for ${environment} environment`,
      );
    } catch (error) {
      console.error("[ErrorTracker] Failed to initialize Sentry:", error);
      this.sentryEnabled = false;
    }
  }

  private setupGlobalHandlers() {
    // Fallback handlers when Sentry is not enabled
    window.addEventListener("unhandledrejection", (event) => {
      console.error(
        "[ErrorTracker] Unhandled promise rejection:",
        event.reason,
      );
    });

    window.addEventListener("error", (event) => {
      console.error(
        "[ErrorTracker] Global error:",
        event.error || event.message,
      );
    });
  }

  captureException(error: Error, context?: ErrorContext) {
    if (!this.initialized) {
      this.init();
    }

    // Always log to console in development
    if (import.meta.env.DEV) {
      console.error("[ErrorTracker]", error, context);
    }

    // Send to Sentry if enabled
    if (this.sentryEnabled) {
      Sentry.captureException(error, {
        tags: context?.tags,
        extra: context?.extra,
        contexts: context?.contexts,
      });
    }
  }

  captureMessage(
    message: string,
    level: "info" | "warning" | "error" = "info",
  ) {
    if (!this.initialized) {
      this.init();
    }

    if (import.meta.env.DEV) {
      console.error(`[ErrorTracker] ${level}:`, message);
    }

    if (this.sentryEnabled) {
      Sentry.captureMessage(message, level);
    }
  }

  setUser(user: { id: string; email?: string; role?: string; name?: string }) {
    if (this.sentryEnabled) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
        role: user.role,
      });
    }
  }

  clearUser() {
    if (this.sentryEnabled) {
      Sentry.setUser(null);
    }
  }

  addBreadcrumb(
    message: string,
    category?: string,
    level?: "info" | "warning" | "error",
  ) {
    if (this.sentryEnabled) {
      Sentry.addBreadcrumb({
        message,
        category: category || "user-action",
        level: level || "info",
        timestamp: Date.now() / 1000,
      });
    }
  }

  setContext(name: string, context: Record<string, unknown>) {
    if (this.sentryEnabled) {
      Sentry.setContext(name, context);
    }
  }

  setTag(key: string, value: string) {
    if (this.sentryEnabled) {
      Sentry.setTag(key, value);
    }
  }
}

export const errorTracker = new ErrorTracker();

// Make available globally for ErrorBoundary and other components
if (typeof window !== "undefined") {
  window.errorTracker = {
    captureException: (error: Error, context?: ErrorContext) => {
      errorTracker.captureException(error, context);
    },
  };
}
